import { type ProfileTemplatePublishStatus } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Popover, PopoverPortal, PopoverTrigger } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { Search, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { TemplateMemberValueCell } from '@/core/components/profile-templates/template-member-value-cell';
import type { TemplateMemberValuesDict } from '@/core/components/profile-templates/template-member-value-inputs';
import { TemplateMemberValuesCsvActions } from '@/core/components/profile-templates/template-member-values-csv-actions';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useTemplateColumnMemberValues } from '@/core/hooks/use-template-column-member-values';
import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';
import { profileCellDiffersFromStandard } from '@/core/lib/template-member-value-column-api';
import { memberMatchesSearch } from '@/core/lib/template-member-values-editor';

type MemberFilter = 'all' | 'overrides' | 'noOverrides';

const MOBILE_PANEL_CLASS =
  'fixed z-[100] flex flex-col gap-0 overflow-hidden rounded-lg border bg-popover shadow-lg outline-none ' +
  'top-[calc(env(safe-area-inset-top,0px)+5dvh)] bottom-[calc(env(safe-area-inset-bottom,0px)+5dvh)] ' +
  'left-[calc(env(safe-area-inset-left,0px)+5vw)] right-[calc(env(safe-area-inset-right,0px)+5vw)] ' +
  'h-auto w-auto max-h-none max-w-none';

const DESKTOP_PANEL_CLASS =
  'fixed left-1/2 top-1/2 z-[100] flex h-[80dvh] max-h-[80dvh] w-[80vw] max-w-[80vw] -translate-x-1/2 -translate-y-1/2 flex-col gap-0 overflow-hidden rounded-lg border bg-popover shadow-lg outline-none';

export type TemplateMemberValuesTablePopoverProps = {
  column: TemplateMemberValueColumn;
  title: string;
  workspaceId: string;
  templateId: string;
  publishStatus: ProfileTemplatePublishStatus;
  roster: MemberRosterRow[];
  rosterLoading?: boolean;
  hasTeams: boolean;
  canManage: boolean;
  dict: TemplateMemberValuesDict;
  defaultValue?: string;
  maxLength?: number;
  placeholder?: string;
  disabled?: boolean;
};

function filterLabel(
  filter: MemberFilter,
  dict: TemplateMemberValuesTablePopoverProps['dict'],
  counts: { all: number; overrides: number; noOverrides: number },
): string {
  switch (filter) {
    case 'overrides':
      return `${dict.filterOverrides} (${counts.overrides})`;
    case 'noOverrides':
      return `${dict.filterNoOverrides} (${counts.noOverrides})`;
    default:
      return `${dict.filterAll} (${counts.all})`;
  }
}

function cellValueForUser(
  values: Record<string, string>,
  userId: string,
  defaultDisplay: string,
): string {
  if (Object.prototype.hasOwnProperty.call(values, userId)) {
    return values[userId] ?? '';
  }
  return defaultDisplay;
}

export function TemplateMemberValuesTablePopover({
  column,
  title,
  workspaceId,
  templateId,
  publishStatus,
  roster,
  rosterLoading = false,
  hasTeams,
  canManage,
  dict,
  defaultValue = '',
  maxLength,
  placeholder,
  disabled = false,
}: TemplateMemberValuesTablePopoverProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<MemberFilter>('all');

  const {
    values: columnValues,
    loading: valuesLoading,
    loadFailed,
    canEdit,
    updateCell,
    applyBulkUpdates,
    commitChanges,
    discardChanges,
    hasPendingChanges,
    pendingChangesCount,
    saving,
    saveFailed,
  } = useTemplateColumnMemberValues({
    workspaceId,
    templateId,
    column,
    publishStatus,
    open,
  });

  const memberValuesDisabled = disabled || !canManage || !canEdit;

  const handleCloseWithDiscard = useCallback(() => {
    discardChanges();
    setSearch('');
    setFilter('all');
    setOpen(false);
  }, [discardChanges]);

  const handleDone = useCallback(async () => {
    const saved = await commitChanges();
    if (saved) {
      setSearch('');
      setFilter('all');
      setOpen(false);
      return;
    }
    toast.error(dict.loadFailed);
  }, [commitChanges, dict.loadFailed]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleCloseWithDiscard();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [handleCloseWithDiscard, open]);

  const defaultDisplay = useMemo(() => defaultValue.trim(), [defaultValue]);

  const hasDeviation = useCallback(
    (userId: string) =>
      profileCellDiffersFromStandard(
        cellValueForUser(columnValues, userId, defaultDisplay),
        defaultDisplay,
      ),
    [columnValues, defaultDisplay],
  );

  const overrideCount = useMemo(
    () => roster.filter((member) => hasDeviation(member.userId)).length,
    [hasDeviation, roster],
  );

  const noOverrideCount = roster.length - overrideCount;

  const filteredRoster = useMemo(() => {
    return roster.filter((member) => {
      if (!memberMatchesSearch(member, search)) return false;
      if (filter === 'overrides') {
        return hasDeviation(member.userId);
      }
      if (filter === 'noOverrides') {
        return !hasDeviation(member.userId);
      }
      return true;
    });
  }, [filter, hasDeviation, roster, search]);

  const memberCountLabel = (dict.memberCount ?? '{shown} / {total}')
    .replace('{shown}', String(filteredRoster.length))
    .replace('{total}', String(roster.length));

  const overrideCountLabel =
    roster.length > 0
      ? (dict.overrideCountLabel ?? '{count} of {total}')
          .replace('{count}', String(overrideCount))
          .replace('{total}', String(roster.length))
      : null;

  const pendingChangesLabel =
    pendingChangesCount > 0
      ? (dict.pendingChangesCount ?? '{count}').replace(
          '{count}',
          String(pendingChangesCount),
        )
      : null;

  const filterCounts = {
    all: roster.length,
    overrides: overrideCount,
    noOverrides: noOverrideCount,
  };

  const inputPlaceholder =
    placeholder?.trim() ||
    (defaultDisplay
      ? `${dict.usesDefaultPlaceholder}: ${defaultDisplay}`
      : dict.usesDefaultPlaceholder);

  const renderValueCell = (member: MemberRosterRow) => (
    <TemplateMemberValueCell
      memberUserId={member.userId}
      column={column}
      cellValue={cellValueForUser(columnValues, member.userId, defaultDisplay)}
      defaultDisplay={defaultDisplay}
      canManage={canManage && canEdit}
      inputPlaceholder={inputPlaceholder}
      maxLength={maxLength}
      resetLabel={dict.resetToDefault}
      enabledLabel={dict.enabledLabel}
      layout={isMobile ? 'stacked' : 'inline'}
      onUpdate={(value, normalizeWhitespace) =>
        updateCell(member.userId, value, normalizeWhitespace)
      }
    />
  );

  const dataLoading = rosterLoading || (open && canEdit && valuesLoading);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setOpen(true);
          return;
        }
        handleCloseWithDiscard();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={memberValuesDisabled || !hasTeams}
          title={dict.editButtonTitle}
          aria-label={dict.editButtonTitle}
          aria-expanded={open}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-input bg-card text-sm font-medium shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 dark:bg-input/30"
        >
          <Users className="h-4 w-4" />
        </button>
      </PopoverTrigger>

      {open ? (
        <PopoverPortal>
          <div className="contents">
            <div
              className="fixed inset-0 z-[90] bg-black/20"
              aria-hidden
              onClick={handleCloseWithDiscard}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              className={isMobile ? MOBILE_PANEL_CLASS : DESKTOP_PANEL_CLASS}
            >
              <div className="flex shrink-0 items-start gap-2 border-b px-4 py-3">
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-base font-semibold leading-tight">
                    {title}
                  </p>
                  {defaultDisplay ? (
                    <p className="mt-1 rounded-md bg-muted/60 px-2 py-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {dict.defaultForAll}:
                      </span>{' '}
                      <span className="break-words">{defaultDisplay}</span>
                    </p>
                  ) : null}
                  {overrideCountLabel ? (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {overrideCountLabel}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="shrink-0 space-y-2 px-4 pt-3 pb-1">
                {!hasTeams ? (
                  <p className="text-sm text-muted-foreground">
                    {dict.noTeams}
                  </p>
                ) : !canEdit ? (
                  <p className="text-sm text-muted-foreground">
                    {dict.syncingHint}
                  </p>
                ) : saveFailed ? (
                  <p className="text-sm text-destructive">{dict.loadFailed}</p>
                ) : loadFailed ? (
                  <p className="text-sm text-destructive">{dict.loadFailed}</p>
                ) : roster.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {dict.noMembers}
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      <TemplateMemberValuesCsvActions
                        column={column}
                        roster={roster}
                        columnValues={columnValues}
                        defaultValue={defaultDisplay}
                        onBulkUpdate={applyBulkUpdates}
                        canManage={canManage && canEdit}
                        disabled={memberValuesDisabled || dataLoading}
                        stacked={isMobile}
                        dict={{
                          exportCsv: dict.exportCsv,
                          importCsv: dict.importCsv,
                          importSuccess: dict.importSuccess,
                          importError: dict.importError,
                        }}
                      />
                      <div className="h-px bg-border/70 mt-2 mb-3" />
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={dict.searchPlaceholder}
                          className="h-9 pl-9"
                          aria-label={dict.searchPlaceholder}
                          disabled={dataLoading}
                        />
                      </div>
                      {isMobile ? (
                        <div className="space-y-1.5">
                          <Select
                            value={filter}
                            disabled={dataLoading}
                            onValueChange={(value) =>
                              setFilter(value as MemberFilter)
                            }
                          >
                            <SelectTrigger
                              className="h-9 w-full"
                              aria-label={dict.filterAll}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[120]">
                              <SelectItem value="all">
                                {filterLabel('all', dict, filterCounts)}
                              </SelectItem>
                              <SelectItem value="overrides">
                                {filterLabel('overrides', dict, filterCounts)}
                              </SelectItem>
                              <SelectItem value="noOverrides">
                                {filterLabel('noOverrides', dict, filterCounts)}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {memberCountLabel}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex rounded-md border p-0.5">
                            <Button
                              type="button"
                              variant={filter === 'all' ? 'secondary' : 'ghost'}
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={dataLoading}
                              onClick={() => setFilter('all')}
                            >
                              {dict.filterAll}
                            </Button>
                            <Button
                              type="button"
                              variant={
                                filter === 'overrides' ? 'secondary' : 'ghost'
                              }
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={dataLoading}
                              onClick={() => setFilter('overrides')}
                            >
                              {dict.filterOverrides}
                              {overrideCount > 0 ? ` (${overrideCount})` : ''}
                            </Button>
                            <Button
                              type="button"
                              variant={
                                filter === 'noOverrides' ? 'secondary' : 'ghost'
                              }
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={dataLoading}
                              onClick={() => setFilter('noOverrides')}
                            >
                              {dict.filterNoOverrides}
                              {noOverrideCount > 0
                                ? ` (${noOverrideCount})`
                                : ''}
                            </Button>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {memberCountLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-0.5 pb-2">
                {!hasTeams || !canEdit || dataLoading || roster.length === 0 ? (
                  dataLoading && canEdit && roster.length > 0 ? (
                    <div className="space-y-2 py-2">
                      <Skeleton className="h-12 w-full rounded-md" />
                      <Skeleton className="h-12 w-full rounded-md" />
                      <Skeleton className="h-12 w-full rounded-md" />
                    </div>
                  ) : null
                ) : filteredRoster.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {dict.noSearchResults}
                  </p>
                ) : isMobile ? (
                  <ul className="divide-y">
                    {filteredRoster.map((member) => {
                      const hasOverride = hasDeviation(member.userId);
                      return (
                        <li
                          key={member.userId}
                          className={cn(
                            'py-3 first:pt-1 last:pb-1',
                            hasOverride && 'rounded-md bg-primary/5 px-2 -mx-2',
                          )}
                        >
                          <p
                            className="truncate text-sm font-medium"
                            title={
                              member.email
                                ? `${member.displayName} (${member.email})`
                                : member.displayName
                            }
                          >
                            {member.displayName}
                          </p>
                          {member.email ? (
                            <p className="truncate text-xs text-muted-foreground">
                              {member.email}
                            </p>
                          ) : null}
                          <div className="mt-2">{renderValueCell(member)}</div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[10rem] w-[38%]">
                          {dict.tableColumnMember}
                        </TableHead>
                        <TableHead>{dict.tableColumnValue}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRoster.map((member) => {
                        const hasOverride = hasDeviation(member.userId);
                        return (
                          <TableRow
                            key={member.userId}
                            className={cn(
                              hasOverride && 'bg-primary/5 hover:bg-primary/10',
                            )}
                          >
                            <TableCell className="align-middle py-2">
                              <p
                                className="truncate text-sm font-medium"
                                title={
                                  member.email
                                    ? `${member.displayName} (${member.email})`
                                    : member.displayName
                                }
                              >
                                {member.displayName}
                              </p>
                              {member.email ? (
                                <p className="truncate text-xs text-muted-foreground">
                                  {member.email}
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell className="align-middle py-2">
                              {renderValueCell(member)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div className="shrink-0 border-t px-4 py-3">
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                  {pendingChangesLabel ? (
                    <p className="text-xs text-muted-foreground sm:mr-auto">
                      {pendingChangesLabel}
                    </p>
                  ) : null}
                  <div className="flex w-full justify-end gap-2 sm:w-auto">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-28"
                      disabled={saving}
                      onClick={handleCloseWithDiscard}
                    >
                      {dict.tableCancel}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      className="w-28 bg-primary-solid text-primary-solid-foreground hover:bg-primary-solid-hover"
                      disabled={saving || !canEdit || !hasPendingChanges}
                      onClick={() => {
                        void handleDone();
                      }}
                    >
                      {dict.tableDone}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PopoverPortal>
      ) : null}
    </Popover>
  );
}
